import { Component, signal, computed, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';

// ──────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────
export type ticket_status   = 'Open' | 'In Progress' | 'Pending' | 'Resolved' | 'Closed';
export type ticket_priority = 'Low' | 'Medium' | 'High' | 'Critical';
export type ticket_category = 'IT Support' | 'Admission' | 'Finance' | 'Library' | 'Transport' | 'Maintenance' | 'HR' | 'Academic' | 'Other';
export type hd_tab          = 'dashboard' | 'tickets' | 'kb';

// ──────────────────────────────────────────────────────────
// INTERFACES
// ──────────────────────────────────────────────────────────
export interface ticket_comment {
  id: number; 
  author: string; 
  author_role: 'requester' | 'agent' | 'erp admin';
  message: string; 
  is_internal: boolean; 
  created_at: string;
}

export interface Ticket {
  id: number; 
  ticket_no: string; 
  title: string; 
  description: string;
  category: ticket_category; 
  priority: ticket_priority; 
  status: ticket_status;
  requester_name: string; 
  requester_dept: string; 
  requester_email: string;
  assigned_to: string; 
  due_date: string; 
  resolution_note: string;
  comments: ticket_comment[]; 
  created_at: string; 
  updated_at: string; 
  resolved_at: any;
  sla_hours: number;
}

export interface KBArticle {
  id: number; 
  title: string; 
  category: string; 
  summary: string;
  content: string; 
  views: number; 
  helpful: number; 
  created_at: string;
}

// ──────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────
@Component({
  selector:    'app-help-desk',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  providers:   [DatePipe],
  templateUrl: './help-desk.html',
  styleUrl:    './help-desk.scss',
})
export class HelpDesk {
  dataService = inject(DataService);
	datePipe = inject(DatePipe);
	loading = false;
	// isEdit = false;
	allData: any = [];
  user: any = null;
	today = new Date().toISOString().split('T')[0];
  base = 'tickets';		// important for API calls

  // ── Tabs ──────────────────────────────────────────────────
  active_tab       = signal<hd_tab>('tickets');
  toast_msg        = signal('');

  // ── Modals ────────────────────────────────────────────────
  show_new_ticket  = signal(false);
  viewing_ticket_id = signal<number | null>(null);
  show_kb_article  = signal<KBArticle | null>(null);

  // ── New ticket form ───────────────────────────────────────
  new_form: Partial<Ticket> = {};
  new_submitted = false;

  // ── Ticket detail form state ──────────────────────────────
  new_comment_text     = '';
  new_comment_internal = false;
  resolve_note         = '';

  // ── Filters ───────────────────────────────────────────────
  tkt_search      = signal('');
  tkt_status_f    = signal<ticket_status | ''>('');
  tkt_priority_f  = signal<ticket_priority | ''>('');
  tkt_category_f  = signal<ticket_category | ''>('');
  tkt_agent_f     = signal('');
  kb_search       = signal('');
  kb_cat_f        = signal('');

  // ── Counters ──────────────────────────────────────────────
  private next_ticket_id  = 21;
  private next_comment_id = 60;

  // ── Data ─────────────────────────────────────────────────
  tickets   = signal<Ticket[]> ([]);        //(this.build_tickets());
  staff     = signal<any> ([]);
  kb_articles = signal<KBArticle[]> (this.build_kb());

  // ── Viewing ticket (reactive from tickets signal) ─────────
  readonly viewing_ticket = computed(() => {
    const id = this.viewing_ticket_id();
    return id ? (this.tickets()?.find(t => t.id === id) ?? null) : null;
  });

  // ── Computed stats ────────────────────────────────────────
  readonly ticket_stats = computed(() => {
    const t   = this.tickets();
    const today = this.today_str();
    return {
      total:          t.length,
      open:           t.filter(x => x.status === 'Open').length,
      in_progress:    t.filter(x => x.status === 'In Progress').length,
      pending:        t.filter(x => x.status === 'Pending').length,
      resolved_today: t.filter(x => x.resolved_at?.startsWith(today)).length,
      overdue:        t.filter(x => this.is_overdue(x)).length,
      resolved:       t.filter(x => x.status === 'Resolved').length,
      closed:         t.filter(x => x.status === 'Closed').length,
    };
  });

  readonly filtered_tickets = computed(() => {
    const q   = this.tkt_search().toLowerCase();
    const order: Record<ticket_priority, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };
    const retVal: any = this.tickets()?.filter(t => {
        const mQ = !q || [t.title, t.ticket_no, t.requester_name, t.requester_dept, t.category, t.assigned_to]
                          .some(v => v.toLowerCase().includes(q));
        return mQ &&
          (!this.tkt_status_f()   || t.status   === this.tkt_status_f())   &&
          (!this.tkt_priority_f() || t.priority === this.tkt_priority_f()) &&
          (!this.tkt_category_f() || t.category === this.tkt_category_f()) &&
          (!this.tkt_agent_f()    || t.assigned_to === this.tkt_agent_f());
      })
      .sort((a, b) => order[a.priority] - order[b.priority] || b.created_at.localeCompare(a.created_at));
    return retVal || [];
  });

  readonly priority_breakdown = computed(() => {
    const active = this.tickets()?.filter(t => t.status !== 'Resolved' && t.status !== 'Closed');
    const total  = active.length || 1;
    return [
      { label: 'Critical', count: active.filter(x => x.priority === 'Critical').length, color: '#dc2626', bg: '#fee2e2' },
      { label: 'High',     count: active.filter(x => x.priority === 'High').length,     color: '#f97316', bg: '#ffedd5' },
      { label: 'Medium',   count: active.filter(x => x.priority === 'Medium').length,   color: '#f59e0b', bg: '#fef3c7' },
      { label: 'Low',      count: active.filter(x => x.priority === 'Low').length,      color: '#22c55e', bg: '#dcfce7' },
    ].map(p => ({ ...p, pct: Math.round((p.count / total) * 100) }));
  });

  readonly category_breakdown = computed(() => {
    const map: Record<string, number> = {};
    this.tickets()?.filter(t => t.status !== 'Closed')
      .forEach(t => { map[t.category] = (map[t.category] ?? 0) + 1; });
    const max = Math.max(...Object.values(map), 1);
    return Object.entries(map)
      .map(([cat, count]) => ({ cat, count, pct: Math.round((count / max) * 100) }))
      .sort((a, b) => b.count - a.count);
  });

  readonly sla_compliance = computed(() => {
    const active = this.tickets()?.filter(t => t.status !== 'Resolved' && t.status !== 'Closed');
    if (!active.length) return 100;
    return Math.round((active.filter(t => !this.is_overdue(t)).length / active.length) * 100);
  });

  readonly recent_tickets = computed(() =>
    this.tickets()
      .filter(t => t.status !== 'Closed')
      .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
      .slice(0, 8)
  );

  readonly overdue_tickets = computed(() =>
    this.tickets()?.filter(t => this.is_overdue(t))
      .sort((a, b) => {
        const ord: Record<ticket_priority, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };
        return ord[a.priority] - ord[b.priority];
      })
  );

  readonly filtered_kb = computed(() => {
    const q = this.kb_search().toLowerCase();
    return this.kb_articles()
      .filter(a =>
        (!q || a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q)) &&
        (!this.kb_cat_f() || a.category === this.kb_cat_f())
      )
      .sort((a, b) => b.views - a.views);
  });

  readonly unique_agents = computed(() =>
    [...new Set(this.tickets()?.map(t => t.assigned_to).filter(Boolean))].sort()
  );
  readonly unique_categories = computed(() =>
    [...new Set(this.tickets()?.map(t => t.category))].sort() as ticket_category[]
  );
  readonly kb_categories = computed(() =>
    [...new Set(this.kb_articles().map(a => a.category))].sort()
  );

  async ngOnInit() {
    let erp_rem: any = localStorage.getItem("erp_rem");
    if(erp_rem) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;
    }

		this.loading = true;
    await this.dataService.getAllRecords('staff').then((res: any) => {
      this.staff.set(res);
      this.loading = false;
    });

		this.loading = true;
    await this.dataService.getAllRecords('tickets').then((res: any) => {
      if(res && res.length) {
        let tkt: any = [];
        this.next_ticket_id = res.length+1;

        if(this.user.user_role[0].code == 'spr') {
          tkt = res.filter((t: any) => {
            return (t.erp_key && t.assigned_to=='') || t.erp_key=='';
          });

          this.tickets.set(tkt);
        } else if(this.user.user_role[0].code == 'adm') {
          tkt = res.filter((t: any) => {
            return (!t.erp_key && t.assigned_to == this.user.emp_code) || (t.erp_key && t.erp_key == this.user.erp_key);
          });

          this.tickets.set(tkt);
        } else {
          // this.tickets.set(res);
          tkt = res.filter((t: any) => {
            return t.assigned_to == this.user.emp_code;
          });

          this.tickets.set(tkt);
        }
      }

      this.loading = false;
    });
  }


  // ── Donut chart ───────────────────────────────────────────
  donut_segs(): { dash: string; offset: string; color: string; label: string; count: number }[] {
    const breakdown = this.priority_breakdown();
    const total = breakdown.reduce((s, p) => s + p.count, 0);
    if (!total) return [];
    const r = 52, c = 2 * Math.PI * r;
    let off = 0;
    return breakdown.map(p => {
      const pct  = p.count / total;
      const dash = (pct * c).toFixed(2);
      const gap  = (c * (1 - pct)).toFixed(2);
      const seg  = { ...p, dash: `${dash} ${gap}`, offset: (-(off * c)).toFixed(2) };
      off += pct;
      return seg;
    });
  }

  // ── New Ticket ────────────────────────────────────────────
  open_new_ticket(): void {
    this.new_form = {
      category: 'IT Support', priority: 'Medium', status: 'Open',
      assigned_to: '', requester_name: '', requester_dept: '', requester_email: '',
      description: '', title: '',
    };
    this.new_submitted = false;
    this.show_new_ticket.set(true);
  }

  save_new_ticket(): void {
    this.new_submitted = true;
    if (!this.new_form.title?.trim() || !this.new_form.requester_name?.trim() || !this.new_form.category || !this.new_form.description?.trim()) return;
    const n   = this.next_ticket_id++;
    const now = new Date().toISOString();
    const sla = this.get_sla_hours(this.new_form.priority ?? 'Medium');
    const due = new Date(Date.now() + sla * 3600000).toISOString().substring(0, 16);
    const numbers = '0123456789';
    const customCode = this.dataService.getUUID(5, numbers);
    // let t: Ticket = {
    let t: any = {
      id:               n, 
      ticket_no:        `TKT-${new Date().getFullYear()}-`+customCode,
      title:            this.new_form.title!,
      description:      this.new_form.description!,
      category:         this.new_form.category ?? 'Other',
      priority:         this.new_form.priority ?? 'Medium',
      status:           'Open',
      requester_name:   this.new_form.requester_name!,
      requester_dept:   this.new_form.requester_dept ?? '',
      requester_email:  this.new_form.requester_email ?? '',
      assigned_to:      this.new_form.assigned_to ?? '',
      due_date:         due,
      resolution_note:  '',
      comments:         '[]',
      created_at:       now, 
      updated_at:       now, 
      resolved_at:      null, 
      sla_hours:        sla,
      erp_key:          this.user.erp_key || ''
    };

    this.doSave(this.base, t);

    this.tickets.update(l => [t, ...l]);
    this.show_new_ticket.set(false);
    this.flash(`Ticket ${t.ticket_no} created.`);
  }

  // ── Ticket Actions ────────────────────────────────────────
  open_ticket(t: Ticket): void {
    this.viewing_ticket_id.set(t.id);
    this.new_comment_text     = '';
    this.new_comment_internal = false;
    this.resolve_note         = t.resolution_note ?? '';
  }

  update_status(id: number, status: ticket_status): void {
    const now = new Date().toISOString();
    this.tickets.update(l => l.map(t => t.id === id
      ? { ...t, status, updated_at: now, ...(status === 'Resolved' ? { resolved_at: now } : {}) }
      : t
    ));

    this.tickets()?.find((obj: any) => {
      if(obj.id == id) {
        obj.comments = JSON.stringify(obj.comments);
        obj.status = status;
        obj.resolved_at = status == 'Resolved' ? now : null;
        obj.updated_at = now;

        this.doUpdate('tickets', obj);
      }
    });

    this.flash_quiet(`Status → ${status}`);
  }

  assign_ticket(id: number, agent: string): void {
    const now = new Date().toISOString();
    this.tickets.update(l => l.map(t => t.id === id ? { ...t, assigned_to: agent, updated_at: now } : t));

    this.tickets()?.find((obj: any) => {
      if(obj.id == id) {
        obj.assigned_to = agent;
        obj.updated_at = now;

        this.doUpdate('tickets', obj);
      }
    });

    this.flash_quiet(`Assigned to ${agent || 'Unassigned'}`);
  }

  change_priority(id: number, priority: ticket_priority): void {
    const sla = this.get_sla_hours(priority);
    const due = new Date(Date.now() + sla * 3600000).toISOString().substring(0, 16);
    const now = new Date().toISOString();
    this.tickets.update(l => l.map(t => t.id === id
      ? { ...t, priority, sla_hours: sla, due_date: due, updated_at: now }
      : t
    ));

    this.tickets()?.find((obj: any) => {
      if(obj.id == id) {
        obj.comments = JSON.stringify(obj.comments);
        obj.priority = priority;
        obj.sla_hours = sla;
        obj.due_date = due;
        obj.updated_at = now;

        this.doUpdate('tickets', obj);
      }
    });

    this.flash_quiet(`Priority → ${priority}`);
  }

  resolve_ticket(id: number): void {
    if (!this.resolve_note.trim()) { this.flash('Please enter a resolution note.'); return; }
    const now = new Date().toISOString();
    const comment: ticket_comment = {
      id: this.next_comment_id++, author: 'Admin User', author_role: 'erp admin',
      message: `✓ Resolved: ${this.resolve_note}`, is_internal: false, created_at: now,
    };
    this.tickets.update(l => l.map(t => t.id === id
      ? { ...t, status: 'Resolved', resolution_note: this.resolve_note, resolved_at: now, updated_at: now,
          comments: [...t.comments, comment] }
      : t
    ));

    this.tickets()?.find((obj: any) => {
      if(obj.id == id) {
        obj.comments = JSON.stringify(obj.comments);
        obj.status = 'Resolved';
        obj.resolution_note = this.resolve_note;
        obj.resolved_at = now;
        obj.updated_at = now;

        this.doUpdate('tickets', obj);
      }
    });

    this.resolve_note = '';
    this.flash(`Ticket resolved.`);
  }

  close_ticket(id: number): void {
    if (!confirm('Close this ticket? It cannot be reopened.')) return;
    this.tickets.update(l => l.map(t => t.id === id
      ? { ...t, status: 'Closed', updated_at: new Date().toISOString() }
      : t
    ));
    this.viewing_ticket_id.set(null);
    this.flash('Ticket closed.');
  }

  reopen_ticket(id: number): void {
    const now = new Date().toISOString();
    this.tickets.update(l => l.map(t => t.id === id
      ? { ...t, status: 'Open', resolved_at: '', updated_at: now }
      : t
    ));
    this.flash('Ticket reopened.');
  }

  add_comment(): void {
    const id = this.viewing_ticket_id();
    if (!id || !this.new_comment_text.trim()) return;
    const now = new Date().toISOString();
    const comment: ticket_comment = {
      id: this.next_comment_id++, author: 'Admin User', author_role: 'erp admin',
      message: this.new_comment_text.trim(),
      is_internal: this.new_comment_internal,
      created_at: now,
    };

    this.tickets()?.find((obj: any) => {
      if(obj.id == id) {
        obj.comments = JSON.stringify([...obj.comments || [], comment]);
        obj.updated_at = now;

        this.doUpdate('tickets', obj);
      }
    });

    this.tickets.update(l => l.map(t => t.id === id
      ? { ...t, comments: [...t.comments, comment], updated_at: now }
      : t
    ));

    this.new_comment_text     = '';
    this.new_comment_internal = false;
  }

  increment_kb_view(id: number): void {
    this.kb_articles.update(l => l.map(a => a.id === id ? { ...a, views: a.views + 1 } : a));
  }

  mark_kb_helpful(id: number): void {
    this.kb_articles.update(l => l.map(a => a.id === id ? { ...a, helpful: a.helpful + 1 } : a));
    this.flash('Marked as helpful. Thank you!');
  }

  open_kb_article(a: KBArticle): void {
    this.increment_kb_view(a.id);
    this.show_kb_article.set(a);
  }

  reset_filters(): void {
    this.tkt_search.set(''); this.tkt_status_f.set(''); this.tkt_priority_f.set('');
    this.tkt_category_f.set(''); this.tkt_agent_f.set('');
  }

  // ── Helpers ───────────────────────────────────────────────
  is_overdue(t: Ticket): boolean {
    if (t.status === 'Resolved' || t.status === 'Closed') return false;
    if (!t.due_date) return false;
    return new Date() > new Date(t.due_date);
  }

  get_sla_hours(p: ticket_priority): number {
    return { Critical: 4, High: 8, Medium: 24, Low: 72 }[p] ?? 24;
  }

  get_priority_class(p: ticket_priority): string {
    return { Critical: 'pr-critical', High: 'pr-high', Medium: 'pr-medium', Low: 'pr-low' }[p] ?? '';
  }

  get_status_class(s: ticket_status): string {
    return { Open: 'st-open', 'In Progress': 'st-inprog', Pending: 'st-pending', Resolved: 'st-resolved', Closed: 'st-closed' }[s] ?? '';
  }

  get_cat_icon(cat: ticket_category | string): string {
    return {
      'IT Support': 'bi-laptop-fill', Admission: 'bi-file-person-fill', Finance: 'bi-cash-coin',
      Library: 'bi-book-fill', Transport: 'bi-bus-front-fill', Maintenance: 'bi-tools',
      HR: 'bi-people-fill', Academic: 'bi-mortarboard-fill', Other: 'bi-three-dots',
    }[cat] ?? 'bi-three-dots';
  }

  get_cat_color(cat: string): string {
    return {
      'IT Support':'#2563eb', Admission:'#7c3aed', Finance:'#059669',
      Library:'#dc2626', Transport:'#d97706', Maintenance:'#6b7280',
      HR:'#0891b2', Academic:'#8b5cf6', Other:'#9ca3af',
    }[cat] ?? '#6b7280';
  }

  get_cat_bg(cat: string): string {
    return {
      'IT Support':'#dbeafe', Admission:'#ede9fe', Finance:'#dcfce7',
      Library:'#fee2e2', Transport:'#fef3c7', Maintenance:'#f3f4f6',
      HR:'#cffafe', Academic:'#f5f3ff', Other:'#f3f4f6',
    }[cat] ?? '#f3f4f6';
  }

  get_author_initials(name: string): string {
    return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  }

  hours_elapsed(iso: string): string {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
    if (diff < 1)  return 'just now';
    if (diff < 24) return `${diff}h ago`;
    const d = Math.floor(diff / 24);
    return `${d}d ago`;
  }

  fmt_date(s: string): string {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  fmt_datetime(s: string): string {
    if (!s) return '—';
    return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  }

  private flash(m: string): void { this.toast_msg.set(m); setTimeout(() => this.toast_msg.set(''), 3000); }
  private flash_quiet(m: string): void { this.toast_msg.set(m); setTimeout(() => this.toast_msg.set(''), 1800); }
  private today_str(): string { return new Date().toISOString().substring(0, 10); }
  private iso_date(offsetHours: number): string {
    return new Date(Date.now() - offsetHours * 3600000).toISOString();
  }

  // ──────────────────────────────────────────────────────────
  // SEED DATA
  // ──────────────────────────────────────────────────────────
/*
  private build_tickets(): Ticket[] {
    const cm = (id: number, author: string, role: 'requester' | 'agent' | 'erp admin',
                msg: string, hOff: number, internal = false): ticket_comment => ({
      id, author, author_role: role, message: msg, is_internal: internal,
      created_at: this.iso_date(hOff),
    });

    const t = (
      id: number, title: string, desc: string, cat: ticket_category, pri: ticket_priority,
      st: ticket_status, req: string, dept: string, email: string, agent: string,
      dueOffH: number, comments: ticket_comment[], slaH: number,
      resNote = '', resAt = ''
    ): Ticket => {
      const now = this.iso_date(slaH + 2);
      const due = new Date(Date.now() + dueOffH * 3600000).toISOString().substring(0, 16);
      return {
        id, ticket_no: `TKT-2026-${String(id).padStart(4, '0')}`,
        title, description: desc, category: cat, priority: pri, status: st,
        requester_name: req, requester_dept: dept, requester_email: email,
        assigned_to: agent, due_date: due, resolution_note: resNote,
        comments, created_at: now, updated_at: this.iso_date(slaH), resolved_at: resAt,
        sla_hours: slaH,
      };
    };

    return [
      t(1,  'Network outage in Computer Lab Room 201',
           'All computers in Room 201 lost internet connectivity since 8 AM today. Students unable to access online learning platforms. Router indicator lights show no connection.',
           'IT Support', 'Critical', 'In Progress', 'Harish Nair', 'IT Dept', 'harish@school.in', 'Ashish Pawar', -2,
           [cm(1,'Harish Nair','requester','Issue started at 8 AM. All 30 machines affected.',26), cm(2,'Ashish Pawar','agent','Checked switch – appears the uplink cable is damaged. Ordering replacement.',24)], 4),

      t(2,  'Canteen payment terminal not working',
           'The POS machine at the canteen counter shows "Service Unavailable" since yesterday evening. Students are unable to make payments and must use cash only. Needs urgent attention.',
           'Finance', 'Critical', 'In Progress', 'Ramesh Joshi', 'Canteen', 'canteen@school.in', 'Kavita Singh', -1,
           [cm(3,'Ramesh Joshi','requester','Terminal offline since 5 PM yesterday.',36), cm(4,'Kavita Singh','agent','Contacted payment gateway support. Ticket raised. ETA 4 hours.',24)], 4),

      t(3,  'Salary slip for July 2026 not generated',
           'Staff salary slips for July 2026 have not been uploaded to the employee portal. Multiple staff members have raised this concern. Last month\'s slips were available by the 5th.',
           'HR', 'High', 'Pending', 'Arun Kumar', 'HR', 'hr@school.in', 'Kavita Singh', 8,
           [cm(5,'Arun Kumar','requester','At least 8 staff have reported this.',48), cm(6,'Kavita Singh','agent','Payroll system under maintenance. Will be resolved by Aug 20.', 24, true)], 8),

      t(4,  'Projector bulb blown in Class 11-A',
           'The overhead projector in Class 11-A stopped working mid-session. The bulb has burnt out. Teachers are unable to show presentation slides. Class uses projector daily for 4 subjects.',
           'Maintenance', 'High', 'Open', 'Meera Pillai', 'Science Dept', 'meera@school.in', 'Suresh Patel', 5,
           [cm(7,'Meera Pillai','requester','Happened during Physics class today at 10 AM.',12)], 8),

      t(5,  'Fee payment portal shows gateway error on credit card',
           'Parents attempting to pay fees via credit card on the portal receive "Payment Gateway Timeout" after entering OTP. Debit card and UPI payments work fine. Affects 12+ parents today.',
           'Finance', 'High', 'In Progress', 'Kavita Singh', 'Finance', 'accounts@school.in', 'Ashish Pawar', 6,
           [cm(8,'Kavita Singh','requester','12 parents already reported this today.',18), cm(9,'Ashish Pawar','agent','Coordinating with payment gateway team. Temporary fix: ask parents to use UPI.',14)], 8),

      t(6,  'CCTV camera offline near Main Gate',
           'The security camera mounted at the main entrance (Camera 3) shows "No Signal" on the monitoring dashboard. Head of security flagged this as a safety concern.',
           'IT Support', 'High', 'Open', 'Rajiv Tiwari', 'Security', 'security@school.in', 'Ashish Pawar', 4,
           [cm(10,'Rajiv Tiwari','requester','Camera was working yesterday. Sudden signal loss this morning.',10)], 8),

      t(7,  'Attendance records incorrect for Class IX-B (July)',
           'Monthly attendance report for Class IX-B shows incorrect data for July – 5 students marked absent on July 15 despite being present. Exam committee is using these records for eligibility.',
           'Academic', 'High', 'In Progress', 'Sunita Verma', 'English Dept', 'sunita@school.in', 'Priya Nair', 12,
           [cm(11,'Sunita Verma','requester','The 5 affected students are Rahul, Vikram, Pooja, Anil, Radha.',22), cm(12,'Priya Nair','agent','Admin team is verifying against paper registers. Will correct by EOD.',18)], 8),

      t(8,  'Wrong marks entered in online marksheet for Class XI exam',
           'Ravi Gupta (Roll 11-A-07) and Rahul Mishra (Roll 11-A-09) have incorrect marks in the Chemistry internal assessment uploaded on the portal. Ravi\'s score should be 38 not 28.',
           'Academic', 'High', 'Open', 'Anjali Shah', 'Science Dept', 'anjali@school.in', 'Priya Nair', 10,
           [cm(13,'Anjali Shah','requester','I submitted correct marks 42/50 for both students. Portal shows wrong values.',8)], 8),

      t(9,  'Printer offline in Admin Office',
           'The HP LaserJet printer in the admin office (adjacent to principal\'s cabin) is showing offline on all connected computers. Urgent as TC/CC letters need to be printed today.',
           'IT Support', 'Medium', 'Open', 'Suresh Patel', 'Administration', 'admin@school.in', 'Ashish Pawar', 20,
           [cm(14,'Suresh Patel','requester','Tried restarting printer and reconnecting USB. Still showing offline.',6)], 24),

      t(10, 'AC not cooling properly in Staff Room',
           'The air conditioning unit in the staff common room has not been cooling properly for the past 3 days. Temperature is around 29°C even with AC running at 18°C setting. Regular maintenance overdue.',
           'Maintenance', 'Medium', 'Open', 'Sunita Verma', 'English Dept', 'sunita@school.in', 'Suresh Patel', 48,
           [cm(15,'Sunita Verma','requester','3 staff fainted due to heat last week. Please prioritize.',72)], 24),

      t(11, 'Bus Route 3 schedule change request',
           'Parents of students on Bus Route 3 (Sector 12 to School) have requested a 15-minute earlier pickup time from 7:15 AM to 7:00 AM due to traffic issues near the highway junction.',
           'Transport', 'Medium', 'Pending', 'Suresh Patel', 'Administration', 'admin@school.in', '', 36,
           [cm(16,'Suresh Patel','requester','18 out of 22 parents on Route 3 have signed the request letter.',96), cm(17,'Suresh Patel','agent','Waiting for transport committee approval. Meeting scheduled for Aug 22.',48)], 24),

      t(12, 'Wi-Fi access not working for new student laptops',
           'Five newly admitted BCA students have been unable to connect their laptops to the school Wi-Fi network. Their devices show "Cannot connect to this network" despite using the correct password.',
           'IT Support', 'Medium', 'In Progress', 'Harsh Agarwal', 'BCA', 'harsh@school.in', 'Ashish Pawar', 30,
           [cm(18,'Harsh Agarwal','requester','All 5 laptops are Windows 11, one is Mac. Same issue on all.',48), cm(19,'Ashish Pawar','agent','The MAC addresses were not whitelisted. Adding them now – should resolve in 30 mins.',36)], 24),

      t(13, 'Library book lost and fine calculation query',
           'Student Vikram Singh (Roll 9-A-02) returned a library book 18 days late. The fine amount shown on the portal (₹720) seems incorrect. Standard rate is ₹2/day. Please verify.',
           'Library', 'Low', 'Resolved', 'Anita Sharma', 'Library', 'library@school.in', 'Anita Sharma', 72,
           [cm(20,'Anita Sharma','requester','The system shows ₹4/day instead of ₹2/day.',168), cm(21,'Anita Sharma','agent','Verified – the rate was updated last term. ₹4/day is correct per updated policy.',144)],
           24, 'Confirmed fine is correctly calculated at ₹4/day as per updated library policy effective June 2026.', this.iso_date(144)),

      t(14, 'New student ID cards required for 8 students',
           'Eight newly admitted students (Classes 9 and BCA) do not have their ID cards yet as they were admitted after the bulk printing run. They face issues during school events and exam entries.',
           'Admission', 'Low', 'Open', 'Suresh Patel', 'Administration', 'admin@school.in', 'Suresh Patel', 96,
           [cm(22,'Suresh Patel','requester','Student list and photos shared via email. Please prioritize BCA students first.',72)], 72),

      t(15, 'Internet slow in Library reading room',
           'The broadband speed in the library reading room is consistently below 2 Mbps during peak hours (10 AM–2 PM). Students researching for projects are severely affected. Router may need upgrade.',
           'IT Support', 'Low', 'Resolved', 'Anita Sharma', 'Library', 'library@school.in', 'Ashish Pawar', 72,
           [cm(23,'Anita Sharma','requester','Speed test shows 1.2 Mbps. Normal should be 50 Mbps.',120), cm(24,'Ashish Pawar','agent','Added a Wi-Fi extender and configured QoS. Speed now at 45 Mbps in peak hours.',96)],
           72, 'Added Wi-Fi range extender near library reading room and applied QoS settings on the router. Speeds verified at 40–50 Mbps.', this.iso_date(96)),

      t(16, 'Water cooler not working near Block B staircase',
           'The water purifier-cum-cooler on Floor 2, Block B staircase is not dispensing water. Tap shows no flow when pressed. Students in Classrooms 201–208 are affected.',
           'Maintenance', 'Medium', 'Closed', 'Suresh Patel', 'Administration', 'admin@school.in', 'Suresh Patel', 0,
           [cm(25,'Suresh Patel','requester','Issue started 2 days ago.',168), cm(26,'Suresh Patel','agent','Filter was clogged. Replaced filter. Unit working now.',144)],
           24, 'Water filter was clogged. Replaced filter cartridge. Unit tested and working.', this.iso_date(144)),

      t(17, 'Transfer Certificate request for Class IX student',
           'Parent of Rahul Kumar (Roll 9-A-01) has submitted a TC application. Student is relocating to Pune. Please process TC and mark sheets as per board requirements.',
           'Admission', 'Low', 'Closed', 'Suresh Patel', 'Administration', 'admin@school.in', 'Priya Nair', 0,
           [cm(27,'Suresh Patel','requester','All dues cleared. NOC from accounts attached.',240), cm(28,'Priya Nair','agent','TC and mark sheet ready. Issued on Aug 10, 2026.',216)],
           72, 'TC and consolidated mark sheet issued. All dues verified cleared. Documents signed by Principal.', this.iso_date(216)),

      t(18, 'Science lab chemicals running critically low',
           'Chemistry lab supplies are almost exhausted – HCl, NaOH, and CuSO4 stocks are below minimum. Class 11 practicals for Term 2 cannot proceed without restocking. Purchase order needed.',
           'Academic', 'Medium', 'Open', 'Anjali Shah', 'Science Dept', 'anjali@school.in', 'Suresh Patel', 40,
           [cm(29,'Anjali Shah','requester','Term 2 practicals start Aug 25. Need restock by Aug 20.',72)], 24),

      t(19, 'Staff leave balance not reflecting in portal',
           'After the July payroll run, several staff members notice their leave balance in the HR portal has not been updated. Casual leave balance for 6 staff still shows previous month\'s values.',
           'HR', 'Medium', 'Pending', 'Arun Kumar', 'HR', 'hr@school.in', 'Kavita Singh', 24,
           [cm(30,'Arun Kumar','requester','Issue affects: Meera Pillai, Sunita Verma, Harish Nair and 3 others.',36), cm(31,'Kavita Singh','agent','HR module sync job failed last run. IT team is investigating.', 24, true)], 24),

      t(20, 'Password reset for student portal (Sneha Patel)',
           'Sneha Patel (BCA-01) is unable to login to the student portal after phone change. OTP going to old mobile number that is no longer active. Needs admin password reset.',
           'IT Support', 'Low', 'Resolved', 'Sneha Patel', 'BCA', 'sneha@school.in', 'Ashish Pawar', 72,
           [cm(32,'Sneha Patel','requester','New number is 9867543210. Identity verified with Aadhar.',80), cm(33,'Ashish Pawar','agent','Password reset done. OTP now goes to new number. Student confirmed access.',72)],
           72, 'Verified identity, updated mobile number in system, reset password. Student confirmed successful login.', this.iso_date(72)),
    ];
  }
*/

  private build_kb(): KBArticle[] {
    const a = (id: number, title: string, cat: string, summary: string, content: string, views: number, helpful: number): KBArticle => ({
      id, title, category: cat, summary, content, views, helpful, created_at: this.iso_date(720),
    });
    return [
      a(1, 'How to Reset Your Student/Staff Portal Password',
        'IT Support',
        'Step-by-step guide to reset your login password for the school ERP portal.',
        `**Password Reset Steps:**\n1. Go to the login page and click "Forgot Password"\n2. Enter your registered email address\n3. Check your email for a 6-digit OTP (valid 10 minutes)\n4. Enter the OTP and set a new password\n5. Password must be 8+ characters with at least 1 number\n\n**If OTP is not received:**\n- Check spam/junk folder\n- Ensure your registered email is correct\n- Contact IT Admin at it@school.in or raise a ticket\n\n**If your mobile number has changed:** Raise an IT Support ticket for admin-assisted reset.`,
        1240, 87),
      a(2, 'Fee Payment Process and Deadlines',
        'Finance',
        'How to pay fees online, accepted payment modes, and late fee policy.',
        `**Accepted Payment Methods:**\n- UPI (Google Pay, PhonePe, Paytm)\n- Debit Card / Credit Card\n- Net Banking\n- DD/Cheque (in person at accounts office)\n\n**How to Pay Online:**\n1. Login to the student portal\n2. Go to Finance → Fee Payment\n3. Select the fee head and academic year\n4. Choose payment mode and complete transaction\n5. Download receipt immediately after payment\n\n**Late Fee Policy:**\n- After due date: ₹50/day late fee applies\n- Fee once paid is non-refundable except in transfer cases\n- For payment issues, email accounts@school.in`,
        980, 72),
      a(3, 'How to Apply for Leave Online (Staff)',
        'HR',
        'Staff leave application process through the HR portal.',
        `**Casual Leave (CL):** Max 12 days/year, max 3 consecutive days\n**Sick Leave (SL):** Max 12 days/year, medical certificate required for 3+ days\n**Earned Leave (EL):** 15 days/year, minimum 7 days notice required\n\n**Steps:**\n1. Login → HR → Leave Management → Apply Leave\n2. Select leave type and date range\n3. Enter reason (medical certificate attachment for SL)\n4. Submit – your class teacher/supervisor receives auto-notification\n5. Track status in "My Leave" section\n\n**Approval SLA:** CL within 2 hours; EL within 1 working day\n\nFor urgent leave, call HR directly at Ext. 105.`,
        645, 58),
      a(4, 'Library Card Issuance and Book Borrowing Rules',
        'Library',
        'How to get a library card and the borrowing policy for books.',
        `**Library Card Issuance:**\n- New students receive library cards during orientation week\n- Lost card replacement: ₹50 fee at accounts → submit receipt at library\n- Card is required for all borrowing\n\n**Borrowing Policy:**\n| User | Books | Duration |\n|------|-------|----------|\n| Students | 2 | 14 days |\n| Teaching Staff | 5 | 30 days |\n| Non-Teaching | 3 | 21 days |\n\n**Late Fine:** ₹4 per book per day\n\n**Library Hours:** Mon-Sat 8 AM – 5 PM\nContact: library@school.in or Ext. 301`,
        523, 41),
      a(5, 'School Bus Routes, Stops and Timings',
        'Transport',
        'All bus routes, pickup/drop times and contact for transport queries.',
        `**Route 1 – North Sector (7:00 AM):** Sector 1 → Sector 5 → Sector 8 → School (Arrival 7:45 AM)\n**Route 2 – East Side (7:10 AM):** Rampur Chowk → City Mall → IT Park → School (Arrival 7:50 AM)\n**Route 3 – West Zone (7:15 AM):** Sector 12 → Highway Crossing → School (Arrival 7:55 AM)\n**Route 4 – South Area (7:05 AM):** Station Road → Civil Lines → School (Arrival 7:45 AM)\n\n**Return Timing:** School departs at 2:30 PM (Tues/Thurs 3:00 PM)\n\n**Transport Contact:** admin@school.in or Ext. 103\n\n**New Route Requests:** Submit to admin with parent signatures (min. 10 students).`,
        412, 34),
      a(6, 'Connecting to School Wi-Fi Network',
        'IT Support',
        'How to connect student/staff devices to the school wireless network.',
        `**Networks Available:**\n- **EduSmart-Staff** – For staff devices (password from IT Admin)\n- **EduSmart-Student** – For student laptops/tablets (default password shared during induction)\n- **EduSmart-Guest** – For visitors (limited access, 4-hour session)\n\n**Connection Steps:**\n1. Enable Wi-Fi on your device\n2. Select the appropriate network\n3. Enter the password and connect\n4. Open browser – you may see a captive portal on first use\n5. Accept the Acceptable Use Policy\n\n**Troubleshooting:**\n- Restart Wi-Fi adapter\n- Forget network and reconnect\n- Check if MAC address is whitelisted (new devices need IT registration)\n- Raise a ticket if problem persists`,
        876, 65),
      a(7, 'Student Admission: Required Documents Checklist',
        'Admission',
        'Complete list of documents required for new student admission.',
        `**Mandatory Documents:**\n- Birth Certificate (original + 2 photocopies)\n- Previous School Transfer Certificate (TC)\n- Last 2 years mark sheets / progress reports\n- 4 passport-size photographs\n- Aadhaar Card of student and parent\n- Address proof (utility bill/Aadhaar)\n- Category certificate (if applicable: SC/ST/OBC)\n\n**For BCA/BBA Programs additionally:**\n- Class 12 mark sheet\n- Migration certificate (if from another board)\n- Domicile certificate (if claiming state quota)\n\n**Document Submission:**\nAll documents to be submitted to Admission Office (Room 5, Ground Floor) between 9 AM – 1 PM on working days.\n\nIncomplete documents will result in provisional admission only.`,
        334, 28),
      a(8, 'How to Download Your Salary Slip (Staff)',
        'HR',
        'Guide for staff to access and download monthly salary slips from the portal.',
        `**Steps to Download Salary Slip:**\n1. Login to the staff portal at portal.school.in\n2. Navigate to HR → Payroll → Salary Slips\n3. Select the month and year from the dropdown\n4. Click "View Slip" to preview\n5. Click "Download PDF" to save locally\n\n**Salary Slips are available:**\n- By the 5th of every month for the previous month\n- Last 12 months' slips are accessible\n- Older records available on request from accounts\n\n**Common Issues:**\n- Slip not generated yet: Check after the 5th\n- Balance doesn't match: Raise an HR ticket\n- Can't login: See password reset article\n\nFor payroll queries: accounts@school.in or Ext. 104`,
        298, 22),
    ];
  }

  // ── ACTIONS ───────────────────────────────────────────
  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        this.ngOnInit();
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        this.ngOnInit();
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

}
